// src/utils/frontAuth.js
//
// frontAuth: type === "admin" kullanıcılar için hiyerarşik alt-roller ve
// sayfa/aksiyon bazlı granular yetki kuralları. TÜM kurallar burada toplanır.
//
// Bu dosya SAF tutulur (React / pageConfig importu yok) ki import döngüsü oluşmasın.
// Sayfa listesi (getAdminPages) pageConfig'ten gelir ve buradaki fonksiyonlara
// parametre olarak geçilir.

// Roller: üstten alta hiyerarşi
export const ROLE_ORDER = ["superadmin", "admin", "customer-relations"];

export const ROLE_HIERARCHY = {
  superadmin: 3,
  admin: 2,
  "customer-relations": 1,
};

export const ROLE_LABELS = {
  superadmin: "Süper Admin",
  admin: "Admin",
  "customer-relations": "Müşteri Temsilcisi",
};

// Bilinmeyen/boş rol → admin. Geriye dönük uyum: frontAuth tanımlanmamış mevcut
// adminler bugünkü gibi tam yetkili kalır.
export const DEFAULT_ADMIN_ROLE = "admin";

// Sınırsız erişim sentinel'i. Bir kullanıcının yetki objesi ALL_ACCESS ise can()/canView()
// HER sayfa/yetenek için koşulsuz true döner. superadmin'e bu atanır; böylece superadmin
// hiçbir kısıtlamaya — bugün ya da gelecekte eklenecek herhangi bir gate'e — takılmaz.
export const ALL_ACCESS = "*";

// ---------------------------------------------------------------------------
// Yetenek (capability) kataloğu
// ---------------------------------------------------------------------------
// Bir sayfanın hangi yetenekleri desteklediği. Yalnız "view"den fazlasına ihtiyaç
// duyan sayfaları burada tanımlıyoruz; tanımlı olmayan her sayfa otomatik olarak
// sadece DEFAULT_CAPABILITIES (view) alır.
//   => Yeni bir admin sayfası eklenince yetki matrisinde elle bir şey yapmadan
//      (view-only) görünür; granular istenirse buraya bir satır eklenir.
export const VIEW_CAPABILITY = { id: "view", label: "Görüntüle" };
export const DEFAULT_CAPABILITIES = [VIEW_CAPABILITY];

// NOT: Burada yalnızca gerçekten ENFORCE edilen yetenekler listelenir; aksi halde matriste
// hiçbir şey yapmayan "hayalet" toggle oluşur. Tanımsız her sayfa view-only alır.
// (Henüz enforce edilmeyenler — örn. firma profili / website-editor düzenleme — eklenince
//  buraya capability'leri ile eklenecek.)
export const PAGE_CAPABILITIES = {
  "firm-list": [
    VIEW_CAPABILITY,
    { id: "add", label: "Firma Ekle" },
    { id: "edit", label: "Profil Düzenle" },
    { id: "bank", label: "Banka Bilgisi" },
    { id: "documents-upload", label: "Evrak/Sözleşme Yükle" },
    { id: "documents-approve", label: "Evrak/Sözleşme Onay/Ret" },
    { id: "user-add", label: "Kullanıcı Ekle" },
    { id: "user-pick", label: "Mevcut Kullanıcı Seç" },
    { id: "user-manage", label: "Kullanıcı Düzenle/Kaldır" },
    { id: "user-mail", label: "Mail Şablonu" },
    // İşaretli değilse firma profilindeki Komisyon sekmesi hiç görünmez.
    // Default yalnız superadmin+admin'de açık (rol şablonu tüm yetenekleri verir).
    { id: "commission", label: "Komisyon Oranını Gör" },
    { id: "customers-view", label: "Müşterileri Gör" },
    { id: "customers-detail", label: "Müşteri Detay" },
  ],
  "warehouse-list": [
    VIEW_CAPABILITY,
    { id: "add", label: "Depo Ekle" },
    { id: "edit", label: "Depo Düzenle" },
    // Menüdeki "Sil" aksiyonu bu yeteneğe bağlı; katalogda tanımlı olmadığı için
    // superadmin dışında HİÇ KİMSEYE görünmüyordu (onay bekleyen depo silinemiyordu).
    { id: "delete", label: "Depo Sil" },
    { id: "price-view", label: "Fiyat Listesi Gör" },
    { id: "price-edit", label: "Fiyat Düzenle" },
    { id: "customers-view", label: "Müşterileri Gör" },
    { id: "customers-detail", label: "Müşteri Detay" },
  ],
  "admin-requests": [
    VIEW_CAPABILITY,
    { id: "send-to-companies", label: "Firmalara Gönder" },
    { id: "view-company-offers", label: "Firma Teklifleri" },
    { id: "request-revision", label: "Revize İste" },
    { id: "create-offer", label: "Teklif Oluştur" },
    { id: "duplicate", label: "Talebi Kopyala" },
    { id: "remove", label: "Talebi Kaldır" },
    { id: "request-offers", label: "Talep Teklifleri" },
  ],
  users: [
    VIEW_CAPABILITY,
    { id: "add", label: "Kullanıcı Ekle" },
    { id: "edit", label: "Düzenle" },
    { id: "create-request", label: "Talep Oluştur" },
    { id: "view-requests", label: "Talepler" },
    { id: "view-offers", label: "Teklifler" },
    { id: "add-operation", label: "Operasyon Ekle" },
    { id: "view-operations", label: "Operasyonlar" },
    { id: "view-warehouses", label: "Depo Bilgileri" },
    { id: "add-room", label: "Depo Ekle" },
    { id: "view-contracts", label: "Sözleşmeler" },
    // Müşteri Profili > "Geçici Şifre" sekmesi. Müşterinin mevcut şifresini GEÇERSİZ kılıp
    // yerine geçici şifre maili gönderir → hesap erişimini etkilediği için default yalnız
    // superadmin+admin (müşteri ilişkileri override'ında yer almadığından onlarda kapalı).
    { id: "temp-password", label: "Geçici Şifre Gönder" },
  ],
  "my-customers": [
    VIEW_CAPABILITY,
    { id: "edit", label: "Düzenle" },
    { id: "create-request", label: "Talep Oluştur" },
    { id: "view-requests", label: "Talepler" },
    { id: "view-offers", label: "Teklifler" },
    { id: "add-operation", label: "Operasyon Ekle" },
    { id: "view-operations", label: "Operasyonlar" },
    { id: "view-warehouses", label: "Depo Bilgileri" },
    { id: "add-room", label: "Depo Ekle" },
    { id: "view-contracts", label: "Sözleşmeler" },
  ],
  "admin-offers": [
    VIEW_CAPABILITY,
    { id: "confirm", label: "Onayla" },
    { id: "reject", label: "Reddet" },
    { id: "create-operation", label: "Operasyon Oluştur" },
    { id: "send", label: "Gönder" },
    { id: "copy-link", label: "Bağlantı Kopyala" },
    { id: "delete", label: "Sil" },
    { id: "revise", label: "Revize Teklif Ver" },
  ],
  "my-operations": [
    VIEW_CAPABILITY,
    { id: "add", label: "Operasyon Ekle" },
    { id: "confirm", label: "Onayla / Reddet" },
    { id: "edit", label: "Operasyon Düzenle" },
    { id: "complete", label: "Operasyonu Tamamla" },
    { id: "cancel", label: "İptal Et" },
    { id: "report", label: "Rapor Gör" },
    { id: "team", label: "Operasyon Ekibi" },
    { id: "customer-info", label: "Müşteri Bilgileri" },
  ],
  "admin-applications": [VIEW_CAPABILITY, { id: "confirm", label: "Onayla / Reddet" }],
  "additional-services": [
    VIEW_CAPABILITY,
    { id: "add", label: "Ekle" },
    { id: "edit", label: "Düzenle" },
    { id: "delete", label: "Sil" },
  ],
  // Mağaza promosyon kodları (/promo-codes, yalnız admin).
  "promo-codes": [
    VIEW_CAPABILITY,
    { id: "add", label: "Ekle" },
    { id: "edit", label: "Düzenle" },
    { id: "delete", label: "Sil" },
  ],
  // 2. el ilan onayı (/listing-approvals, yalnız admin). "approve" yetkisi durum değiştirmeyi de kapsar.
  "listing-approvals": [VIEW_CAPABILITY, { id: "approve", label: "Onayla" }, { id: "reject", label: "Reddet" }],
  // Troy Depo ürün ilanı ekleme (/listing-create, yalnız admin).
  "listing-create": [VIEW_CAPABILITY, { id: "add", label: "Ekle" }],
  // Bildirim Merkezi (/notification-center, yalnız admin): toplu/kişiye bildirim gönderme ve şablon yönetimi.
  "notification-center": [
    VIEW_CAPABILITY,
    { id: "send", label: "Bildirim Gönder" },
    { id: "templates", label: "Şablonları Yönet" },
  ],
  // Mağaza ayarları (/store-settings, yalnız admin): ilan ücreti, indirim, kategori listeleri.
  "store-settings": [VIEW_CAPABILITY, { id: "edit", label: "Düzenle" }],
  // Sözleşmeler: onay/red, imzalı dosya yükleme ve imza silme ayrı yetkilerdir.
  contracts: [
    VIEW_CAPABILITY,
    { id: "approve", label: "Onayla / Reddet" },
    { id: "upload", label: "İmzalı Sözleşme Yükle" },
    { id: "signature-delete", label: "İmza Sil" },
  ],
  // Ödemeler: fatura yükleme ayrı yetkidir. Fatura sekmeleri de ayrı görme yetkileridir:
  // işaretli değilse ilgili fatura listesi (sekme + satırları) hiç görünmez.
  payments: [
    VIEW_CAPABILITY,
    { id: "invoice-upload", label: "Fatura Yükle" },
    { id: "invoice-delete", label: "Fatura Sil" },
    { id: "partner-invoices", label: "Partner Faturalarını Gör" },
    { id: "customer-invoices", label: "Müşteri Faturalarını Gör" },
    // Ödeme geçmişine elle borç ekleme/düzenleme/silme (finansal → default yalnız
    // superadmin+admin; müşteri ilişkileri override'ında yer almadığı için onlarda kapalı).
    { id: "manual-payment", label: "Manuel Borç (Ekle/Düzenle/Sil)" },
    // Hakedişler sekmesi: partnere yatan tutar ve yatış tarihi (finansal → default yalnız
    // superadmin+admin; BE payments::payoutsAllowed aynı kuralı uygular).
    { id: "payouts", label: "Hakedişleri Gör" },
  ],
  // Yetki yönetim sayfası: görüntüleme + yetki yönetme + yönetici ekle/işten ayrıldı.
  // Son üçü SÜPER ADMİN'E ÖZEL varsayılandır (SUPERADMIN_ONLY_CAPABILITIES).
  "user-permissions": [
    VIEW_CAPABILITY,
    { id: "manage", label: "Yetki Yönet" },
    { id: "user-add", label: "Kullanıcı Ekle" },
    { id: "user-offboard", label: "İşten Ayrıldı" },
  ],
  // Depo Bilgilerim (müşteri sayfası; admin /my-warehouses/:userID ile erişir).
  // Snapshot güncelleme yetkisi default superadmin+admin'de açık, CR'da kapalı.
  "my-warehouses": [VIEW_CAPABILITY, { id: "snapshot-update", label: "Depo Bilgisi Güncelle" }],
};

// Varsayılanda YALNIZ Süper Admin'de açık yetenekler: admin şablonuna ve admin'in
// geriye dönük tamamlamasına girmez; Süper Admin olmayan kimse bunları veremez/alamaz
// (backend users::updateUser hedefin mevcut değerini korur).
export const SUPERADMIN_ONLY_CAPABILITIES = {
  "user-permissions": ["user-add", "user-offboard"],
};

export function isSuperadminOnlyCapability(pageKey, capId) {
  return (SUPERADMIN_ONLY_CAPABILITIES[pageKey] || []).includes(capId);
}

export function getCapabilities(pageKey) {
  return PAGE_CAPABILITIES[pageKey] || DEFAULT_CAPABILITIES;
}

// ---------------------------------------------------------------------------
// Capability bağımlılıkları (cross-page / intra-page)
// ---------------------------------------------------------------------------
// Bir aksiyon, başka bir sayfaya yönlendirdiği ya da başka bir aksiyonun içinden
// açıldığı için yalnız hedef (page, cap) AÇIKKEN verilebilir. Hedef kapanınca bağımlı
// yetki otomatik düşer ve editörde tekrar açılamaz (kilitli görünür).
//   { [pageKey]: { [capId]: { page, cap } } }
export const CAPABILITY_DEPENDENCIES = {
  "admin-requests": {
    // "Talep Teklifleri" Teklifler sayfasına yönlendirir → admin-offers görünür olmalı.
    "request-offers": { page: "admin-offers", cap: "view" },
    // "Revize İste" Firma Teklifleri menüsü içinden açılır → aynı sayfada o yetki olmalı.
    "request-revision": { page: "admin-requests", cap: "view-company-offers" },
  },
  "admin-offers": {
    // "Operasyon Oluştur" Operasyonlar sayfasını gerektirir.
    "create-operation": { page: "my-operations", cap: "view" },
  },
  // Kullanıcılar/Müşteriler detayındaki navlar hedef sayfanın görünürlüğüne bağlı.
  users: {
    "view-requests": { page: "admin-requests", cap: "view" },
    "view-offers": { page: "admin-offers", cap: "view" },
    "view-operations": { page: "my-operations", cap: "view" },
    "add-operation": { page: "my-operations", cap: "view" },
  },
  "my-customers": {
    "view-requests": { page: "admin-requests", cap: "view" },
    "view-offers": { page: "admin-offers", cap: "view" },
    "view-operations": { page: "my-operations", cap: "view" },
    "add-operation": { page: "my-operations", cap: "view" },
  },
};

export function getCapabilityDependency(pageKey, capId) {
  return CAPABILITY_DEPENDENCIES[pageKey]?.[capId] || null;
}

// Bir capability'nin bağımlılığı karşılanıyor mu? (bağımlılık yoksa daima true)
export function isCapabilityDependencyMet(permissions, pageKey, capId) {
  const dep = getCapabilityDependency(pageKey, capId);
  if (!dep) return true;
  if (permissions === ALL_ACCESS) return true; // superadmin
  const caps = permissions && typeof permissions === "object" ? permissions[dep.page] : null;
  return Array.isArray(caps) && caps.includes(dep.cap);
}

// Bağımlılığı karşılanmayan tüm capability'leri kaldırır (zincirleme deps için sabit noktaya
// kadar tekrarlar). Editörde her değişiklikten sonra çağrılır → tutarlı durum garanti edilir.
export function applyCapabilityDependencies(permissions) {
  if (!permissions || typeof permissions !== "object") return permissions;
  let result = { ...permissions };
  let changed = true;
  while (changed) {
    changed = false;
    for (const [pageKey, deps] of Object.entries(CAPABILITY_DEPENDENCIES)) {
      for (const capId of Object.keys(deps)) {
        const caps = result[pageKey];
        if (!Array.isArray(caps) || !caps.includes(capId)) continue;
        if (!isCapabilityDependencyMet(result, pageKey, capId)) {
          result = { ...result, [pageKey]: caps.filter((c) => c !== capId) };
          changed = true;
        }
      }
    }
  }
  return result;
}

export function getCapabilityIds(pageKey) {
  return getCapabilities(pageKey).map((c) => c.id);
}

// ---------------------------------------------------------------------------
// Format normalizasyonu
// ---------------------------------------------------------------------------
// Canonical format: { [pageKey]: string[] }  (örn. { "users": ["view", "edit"] })
// normalizeFrontAuth, legacy/karışık şekilleri de bu canonical forma çevirir:
//   true                 -> ["view"]
//   { read:true, write }  -> ["view"]  (read->view, write->edit alias)
//   ["read", "confirm"]  -> ["view", "confirm"]

const CAPABILITY_ALIASES = {
  read: "view",
  write: "edit",
  create: "add",
};

function normalizeCapabilityId(id) {
  const key = String(id || "").trim();
  return CAPABILITY_ALIASES[key] || key;
}

function uniq(arr) {
  return Array.from(new Set(arr));
}

function normalizePageValue(value) {
  if (!value) return [];
  if (value === true) return ["view"];
  if (Array.isArray(value)) {
    return uniq(value.map(normalizeCapabilityId).filter(Boolean));
  }
  if (typeof value === "object") {
    return uniq(
      Object.entries(value)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => normalizeCapabilityId(k))
        .filter(Boolean)
    );
  }
  if (typeof value === "string") {
    const v = normalizeCapabilityId(value);
    return v ? [v] : [];
  }
  return [];
}

export function normalizeFrontAuth(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const [pageKey, value] of Object.entries(raw)) {
    // BOŞ DİZİ DÜŞÜRÜLMEZ: "bu sayfada hiçbir yetki yok" ile "bu sayfa hiç kaydedilmemiş"
    // aynı şey değil. Boş dizi atıldığında mergeMissingPagePermissions anahtarı "yeni sayfa"
    // sanıp rol varsayılanından geri yazıyor ve KALDIRILAN yetki tam yetkiyle geri geliyordu.
    out[pageKey] = normalizePageValue(value);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Yetki sorgulama
// ---------------------------------------------------------------------------
export function can(frontAuth, pageKey, capability = "view") {
  if (frontAuth === ALL_ACCESS) return true; // superadmin: koşulsuz sınırsız erişim
  const perms = frontAuth && typeof frontAuth === "object" ? frontAuth : {};
  const caps = perms[pageKey];
  if (!Array.isArray(caps)) return false;
  return caps.includes(normalizeCapabilityId(capability));
}

// Kayıtlı yetki objesi, KAYDEDİLDİKTEN SONRA eklenen sayfaların anahtarlarını içermez;
// bu durumda canView false döner ve yeni sayfa herkese kapalı kalır. Bu fonksiyon YALNIZCA
// hiç bulunmayan anahtarları rol default'undan tamamlar. Açıkça kaldırılmış yetkiler
// (anahtar VAR ama dizi boş) olduğu gibi korunur — yani kimseye yetki genişletilmez.
export function mergeMissingPagePermissions(stored, role, pages = []) {
  if (stored === ALL_ACCESS) return stored;
  if (!stored || typeof stored !== "object") return stored;

  const defaults = buildDefaultPermissions(role, pages);
  let merged = null;
  for (const key of Object.keys(defaults)) {
    if (key in stored) continue;
    if (!merged) merged = { ...stored };
    merged[key] = defaults[key];
  }
  // SUPERADMIN/ADMIN rolü tasarım gereği TAM yetkidir: sonradan eklenen YETENEKLER
  // (ör. payments/partner-invoices) kayıtta sayfa anahtarı var diye eksik kalıyordu.
  // Bu rollerde her kayıtlı sayfanın yetenekleri katalogla birleştirilir; diğer
  // rollerde bilinçli kapatılmış yetenekler korunur. (pages parametresi gerektirmez,
  // AuthContext hydrate'inde de çağrılır.)
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === "admin" || normalizedRole === "superadmin") {
    for (const key of Object.keys(merged || stored)) {
      const current = (merged || stored)[key];
      if (!Array.isArray(current)) continue;
      const missing = getCapabilityIds(key).filter(
        (c) => !current.includes(c) && (normalizedRole === "superadmin" || !isSuperadminOnlyCapability(key, c))
      );
      if (missing.length === 0) continue;
      if (!merged) merged = { ...stored };
      merged[key] = [...current, ...missing];
    }
  }
  return merged || stored;
}

export function canView(frontAuth, pageKey) {
  return can(frontAuth, pageKey, "view");
}

// ---------------------------------------------------------------------------
// Rol yardımcıları & hiyerarşi kuralları
// ---------------------------------------------------------------------------
export function normalizeRole(role) {
  const r = String(role || "").trim();
  return ROLE_HIERARCHY[r] ? r : DEFAULT_ADMIN_ROLE;
}

export function roleLevel(role) {
  return ROLE_HIERARCHY[normalizeRole(role)] || 0;
}

export function isSuperadmin(role) {
  return normalizeRole(role) === "superadmin";
}

// actor, current role'ü targetCurrentRole olan bir kullanıcıyı değiştirebilir mi?
// superadmin herkesi; diğerleri yalnız kendinden DÜŞÜK seviyedekileri değiştirir.
export function canModifyUser(actorRole, targetCurrentRole) {
  if (isSuperadmin(actorRole)) return true;
  return roleLevel(actorRole) > roleLevel(targetCurrentRole);
}

// actor, bir kullanıcıyı hangi rollere atayabilir?
// superadmin: hepsi (superadmin dahil). Diğerleri: kendi seviyesine kadar (dahil).
//   admin -> ["admin", "customer-relations"]  (CR'ı admin yapabilir)
export function assignableRoles(actorRole) {
  if (isSuperadmin(actorRole)) return [...ROLE_ORDER];
  const level = roleLevel(actorRole);
  return ROLE_ORDER.filter((r) => roleLevel(r) <= level);
}

// Yetki yönetim sayfasını kullanabilir mi?
// NOT: "admin" rolüne KOŞULSUZ izin VERME — aksi halde matriste "Yetki Yönet"
// kapatılsa bile admin yine yönetebilir. Yetki yalnız manage capability'sinden gelir.
// Geriye dönük uyum: kayıtlı frontAuth'u olmayan admin, buildDefaultPermissions ile
// zaten manage alır (useFrontAuth/usePageList resolved permissions'ı geçirir).
export function canManagePermissions(frontAuth, role) {
  if (frontAuth === ALL_ACCESS) return true; // superadmin
  if (isSuperadmin(role)) return true; // güvenlik kemeri: superadmin her zaman
  return can(frontAuth, "user-permissions", "manage");
}

// ---------------------------------------------------------------------------
// Rol default yetki şablonları
// ---------------------------------------------------------------------------
// adminPages: [{ key, capabilities: [{id,label}] }]  (pageConfig.getAdminPages())
//
// superadmin & admin -> tüm sayfalarda tüm yetenekler.
// customer-relations -> aşağıdaki sayfalar default açık gelir ve bu sayfalarda
// desteklenen tüm yetenekler default verilir (UI'da düzenlenebilir; bu sadece
// varsayılan başlangıç şablonu).
export const CUSTOMER_RELATIONS_DEFAULT_PAGE_KEYS = [
  "warehouse-list",
  "admin-requests",
  "admin-offers",
  "my-operations",
  // Sözleşmeler: müşteri ilişkileri rolü de müşteri sözleşmelerini görüntüleyebilir.
  "contracts",
  // Ödemeler: müşteri ilişkileri görüntüleyebilir (fatura yükleme finansal — default kapalı).
  "payments",
  "users",
  "my-customers",
  // Depo Bilgilerim: müşteri temsilcisi default görebilsin ve "Depo Bilgisi Güncelle"yi
  // (snapshot-update) kullanabilsin — override yok, iki yetenek de default açık gelir.
  "my-warehouses",
  "chat",
];

// Bazı sayfalarda müşteri ilişkileri rolü varsayılan olarak TÜM yetenekleri almaz.
// (Sözleşme onay/reddi finansal-hukuki sonuç doğurur → varsayılan yalnızca görüntüleme.)
const CUSTOMER_RELATIONS_DEFAULT_CAPABILITY_OVERRIDES = {
  // Sözleşmeler: görüntüleme + Onayla/Reddet + İmzalı Sözleşme Yükle default açık;
  // İmza Sil default kapalı kalır.
  contracts: ["view", "approve", "upload"],
  // Müşteri temsilcisi partnerlerin MÜŞTERİYE kestiği faturaları görebilir; Troy Depo'nun
  // partnere kestikleri (partner-invoices) default yalnız superadmin+admin'de açıktır.
  payments: ["view", "customer-invoices"],
  // Depo Listesi: müşteri temsilcisinde default salt görüntüleme + müşteri/fiyat listesi;
  // depo ekle/düzenle/sil ve fiyat düzenle default KAPALI (operasyonel değişiklikler değil).
  "warehouse-list": ["view", "price-view", "customers-view", "customers-detail"],
  // Kullanıcılar: "Geçici Şifre Gönder" DIŞINDA tüm yetenekler açık. Geçici şifre müşterinin
  // mevcut şifresini geçersiz kıldığı için default yalnız superadmin+admin'de.
  // NOT: bu liste açık uçlu değil — users'a yeni bir yetenek eklenirse müşteri temsilcisine
  // vermek isteniyorsa buraya da eklenmeli.
  users: [
    "view", "add", "edit", "create-request", "view-requests", "view-offers",
    "add-operation", "view-operations", "view-warehouses", "add-room", "view-contracts",
  ],
};

export const CUSTOMER_RELATIONS_DEFAULTS = Object.fromEntries(
  CUSTOMER_RELATIONS_DEFAULT_PAGE_KEYS.map((pageKey) => [
    pageKey,
    CUSTOMER_RELATIONS_DEFAULT_CAPABILITY_OVERRIDES[pageKey] || getCapabilityIds(pageKey),
  ])
);

export function buildDefaultPermissions(role, adminPages = []) {
  const normalizedRole = normalizeRole(role);

  if (normalizedRole === "superadmin" || normalizedRole === "admin") {
    const out = {};
    for (const page of adminPages) {
      const ids = (page.capabilities || DEFAULT_CAPABILITIES)
        .map((c) => c.id)
        .filter((id) => normalizedRole === "superadmin" || !isSuperadminOnlyCapability(page.key, id));
      out[page.key] = ids.length ? ids : ["view"];
    }
    return out;
  }

  if (normalizedRole === "customer-relations") {
    const out = {};
    for (const page of adminPages) {
      const allowed = CUSTOMER_RELATIONS_DEFAULTS[page.key];
      if (!allowed) continue;
      const valid = (page.capabilities || DEFAULT_CAPABILITIES).map((c) => c.id);
      const caps = allowed.filter((c) => valid.includes(c));
      if (caps.length) out[page.key] = caps;
    }
    return out;
  }

  return {};
}
